import { lazy, Suspense, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Navigation from '@/components/Navigation';
import Hero from '@/components/Hero';
import { useLanguage } from '@/contexts/LanguageContext';

// Helper to handle chunk loading failures (stale cache after deployment)
const lazyWithRetry = (componentImport: () => Promise<any>) =>
  lazy(() =>
    componentImport().catch(() => {
      // Chunk failed to load - likely stale cache, refresh the page
      window.location.reload();
      return componentImport();
    })
  );

// Lazy load ALL below-fold components for better LCP and reduced bundle size
const HeroGallery = lazyWithRetry(() => import('@/components/HeroGallery'));
const About = lazyWithRetry(() => import('@/components/About'));
const Testimonials = lazyWithRetry(() => import('@/components/Testimonials'));
const FAQ = lazyWithRetry(() => import('@/components/FAQ'));
const VagasMotorista = lazyWithRetry(() => import('@/components/VagasMotorista'));
const DriverSection = lazyWithRetry(() => import('@/components/DriverSection'));
const CoverageAreas = lazyWithRetry(() => import('@/components/CoverageAreas'));
const QuickLinks = lazyWithRetry(() => import('@/components/QuickLinks'));
const LazyBlogSection = lazyWithRetry(() => import('@/components/LazyBlogSection'));
const Footer = lazyWithRetry(() => import('@/components/DeferredFooter'));
const FloatingWhatsApp = lazyWithRetry(() => import('@/components/FloatingWhatsApp'));

// HARDCODED Portuguese SEO metadata - Google must always see PT
const SEO = {
  title: 'KAREEM TAXI – Parceiro Oficial Yango em Maputo',
  description: 'Kareem Taxi – Parceiro Oficial Yango em Maputo. Cadastro motorista em 10 min, recarga automática M-Pesa/eMola, suporte 24/7. Comece a ganhar hoje!',
  keywords: 'yango maputo, yango moçambique, yango mocambique, yango mozambique, yango taxi maputo, cadastro motorista yango maputo, inscrição motorista yango, criar conta yango, registro yango moçambique, trabalhar com yango, motorista yango maputo, yango delivery maputo, yango taxi moçambique, yango tuktuk, yango pro download, parceiro oficial yango, maputo kareem taxi, kareem taxi parceiro yango, maputo taxi online, ganhar dinheiro yango, maputo motorista aplicativo, maputo yango partner',
  ogDescription: 'Kareem Taxi – Parceiro Oficial Yango em Maputo. Cadastro rápido em Matola e arredores, recargas automáticas M-Pesa/eMola e suporte 24/7.'
};

const Index = () => {
  const { language, t } = useLanguage();

  // HARDCODED Portuguese JSON-LD schemas - Google must always see PT
  const breadcrumbSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Início", "item": "https://www.kareemtaxi.co.mz/" }
    ]
  }), []);


  const organizationSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.kareemtaxi.co.mz/org",
    "name": "KAREEM TAXI – Parceiro Oficial Yango",
    "alternateName": "Kareem Taxi Maputo",
    "url": "https://www.kareemtaxi.co.mz/",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.kareemtaxi.co.mz/images/kareem-taxi-parceiro-oficial-yango-mocambique.webp",
      "width": 400,
      "height": 400,
      "copyrightHolder": { "@type": "Organization", "name": "Kareem Taxi", "url": "https://www.kareemtaxi.co.mz/" },
      "creditText": "KAREEM TAXI – Parceiro Oficial Yango",
      "copyrightNotice": "© 2025 KAREEM TAXI",
      "license": "https://www.kareemtaxi.co.mz/termos-de-uso#imagens",
      "acquireLicensePage": "https://api.whatsapp.com/send?phone=258853448609&text=Gostaria%20de%20adquirir%20licença%20para%20uso%20de%20imagem",
      "creator": { "@type": "Organization", "name": "Kareem Taxi – Parceiro Oficial Yango em Maputo", "url": "https://www.kareemtaxi.co.mz/" }
    },
    "telephone": "+258853448609",
    "description": "Parceiro oficial Yango em Maputo. Cadastro rápido, recargas automáticas M-Pesa/eMola e suporte 24/7 para motoristas.",
    "sameAs": [
      "https://www.facebook.com/kareemtaxi",
      "https://www.instagram.com/kareem.taxi/",
      "https://www.tiktok.com/@kareem.taxi",
      "https://www.pinterest.com/YangoKareemtaximz/",
      "https://x.com/Kareemtaximz"
    ],
    "contactPoint": [
      { "@type": "ContactPoint", "telephone": "+258853448609", "contactType": "customer support", "availableLanguage": ["Portuguese", "English"], "areaServed": "MZ" },
      { "@type": "ContactPoint", "telephone": "+258856764504", "contactType": "WhatsApp", "availableLanguage": ["Portuguese", "English"], "areaServed": "MZ" }
    ]
  }), []);

  const localBusinessSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "TaxiService"],
    "@id": "https://www.kareemtaxi.co.mz/business",
    "parentOrganization": { "@id": "https://www.kareemtaxi.co.mz/org" },
    "name": "KAREEM TAXI – Parceiro Oficial Yango",
    "alternateName": "KAREEM TAXI – Parceiro Oficial Yango em Maputo",
    "foundingDate": "2024-03-15",
    "description": "Parceiro oficial Yango em Maputo. Cadastro rápido, recargas automáticas M-Pesa/eMola, treinamento Yango Pro e suporte 24/7.",
    "url": "https://www.kareemtaxi.co.mz/",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.kareemtaxi.co.mz/images/kareem-taxi-parceiro-oficial-yango-mocambique.webp",
      "width": 400,
      "height": 400,
      "copyrightHolder": { "@type": "Organization", "name": "Kareem Taxi", "url": "https://www.kareemtaxi.co.mz/" },
      "creditText": "KAREEM TAXI – Parceiro Oficial Yango",
      "copyrightNotice": "© 2025 KAREEM TAXI",
      "license": "https://www.kareemtaxi.co.mz/termos-de-uso#imagens",
      "acquireLicensePage": "https://api.whatsapp.com/send?phone=258853448609&text=Gostaria%20de%20adquirir%20licença%20para%20uso%20de%20imagem",
      "creator": { "@type": "Organization", "name": "Kareem Taxi – Parceiro Oficial Yango em Maputo", "url": "https://www.kareemtaxi.co.mz/" }
    },
    "image": [
      {
        "@type": "ImageObject",
        "url": "https://www.kareemtaxi.co.mz/images/kareem-taxi-parceiro-oficial-yango-mocambique.webp",
        "copyrightHolder": { "@type": "Organization", "name": "Kareem Taxi", "url": "https://www.kareemtaxi.co.mz/" },
        "creditText": "KAREEM TAXI – Parceiro Oficial Yango",
        "copyrightNotice": "© 2025 KAREEM TAXI",
        "license": "https://www.kareemtaxi.co.mz/termos-de-uso#imagens",
        "acquireLicensePage": "https://api.whatsapp.com/send?phone=258853448609&text=Gostaria%20de%20adquirir%20licença%20para%20uso%20de%20imagem",
        "creator": { "@type": "Organization", "name": "Kareem Taxi – Parceiro Oficial Yango em Maputo", "url": "https://www.kareemtaxi.co.mz/" }
      },
      {
        "@type": "ImageObject",
        "url": "https://www.kareemtaxi.co.mz/images/motorista-yango-maputo-1024.avif",
        "copyrightHolder": { "@type": "Organization", "name": "Kareem Taxi", "url": "https://www.kareemtaxi.co.mz/" },
        "creditText": "Motorista Yango Maputo - KAREEM TAXI",
        "copyrightNotice": "© 2025 KAREEM TAXI",
        "license": "https://www.kareemtaxi.co.mz/termos-de-uso#imagens",
        "acquireLicensePage": "https://api.whatsapp.com/send?phone=258853448609&text=Gostaria%20de%20adquirir%20licença%20para%20uso%20de%20imagem",
        "creator": { "@type": "Organization", "name": "Kareem Taxi – Parceiro Oficial Yango em Maputo", "url": "https://www.kareemtaxi.co.mz/" }
      },
      {
        "@type": "ImageObject",
        "url": "https://www.kareemtaxi.co.mz/images/motorista-yango-tuktuk-kareem-taxi-maputo-1024.avif",
        "copyrightHolder": { "@type": "Organization", "name": "Kareem Taxi", "url": "https://www.kareemtaxi.co.mz/" },
        "creditText": "Motorista Yango TukTuk Maputo - KAREEM TAXI",
        "copyrightNotice": "© 2025 KAREEM TAXI",
        "license": "https://www.kareemtaxi.co.mz/termos-de-uso#imagens",
        "acquireLicensePage": "https://api.whatsapp.com/send?phone=258853448609&text=Gostaria%20de%20adquirir%20licença%20para%20uso%20de%20imagem",
        "creator": { "@type": "Organization", "name": "Kareem Taxi – Parceiro Oficial Yango em Maputo", "url": "https://www.kareemtaxi.co.mz/" }
      }
    ],
    "email": "info@kareemtaxi.co.mz",
    "address": { "@type": "PostalAddress", "streetAddress": "Av 24 de Julho, Alto Mae", "addressLocality": "Maputo", "addressRegion": "Maputo", "postalCode": "1204", "addressCountry": "MZ" },
    "geo": { "@type": "GeoCoordinates", "latitude": -25.97121935121613, "longitude": 32.58300286597757 },
    "areaServed": [
      { "@type": "City", "name": "Maputo", "containedIn": { "@type": "Country", "name": "Moçambique" } },
      { "@type": "City", "name": "Matola", "containedIn": { "@type": "Country", "name": "Moçambique" } },
      { "@type": "AdministrativeArea", "name": "Distrito de Boane", "containedIn": { "@type": "Country", "name": "Moçambique" } },
      { "@type": "AdministrativeArea", "name": "Distrito de Marracuene", "containedIn": { "@type": "Country", "name": "Moçambique" } }
    ],
    "priceRange": "$$",
    "currenciesAccepted": "MZN",
    "paymentAccepted": ["M-Pesa", "eMola", "Cash"],
    "amenityFeature": [{ "@type": "LocationFeatureSpecification", "name": "Estacionamento disponível", "value": true }],
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "08:00", "closes": "20:00", "description": "Horário de escritório" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"], "opens": "00:00", "closes": "23:59", "description": "Suporte online disponível 24/7" }
    ],
    "contactPoint": [
      { "@type": "ContactPoint", "telephone": "+258853448609", "contactType": "customer service", "email": "info@kareemtaxi.co.mz", "availableLanguage": ["Portuguese", "English"], "hoursAvailable": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "08:00", "closes": "20:00" } },
      { "@type": "ContactPoint", "telephone": "+258856764504", "contactType": "customer support", "contactOption": "TollFree", "availableLanguage": ["Portuguese", "English"], "hoursAvailable": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"], "opens": "00:00", "closes": "23:59" }, "description": "Suporte WhatsApp 24/7" },
      { "@type": "ContactPoint", "telephone": "+258863213663", "contactType": "sales", "availableLanguage": ["Portuguese", "English"], "hoursAvailable": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "08:00", "closes": "20:00" }, "description": "Linha adicional para consultas" }
    ],
    "sameAs": ["https://www.facebook.com/kareemtaxi.yango/", "https://www.instagram.com/kareem.taxi/", "https://www.tiktok.com/@kareem.taxi", "https://www.pinterest.com/YangoKareemtaxi/", "https://x.com/Kareemtaximz"]
  }), []);

  // Nampula branch LocalBusiness — linked to HQ Organization (parentOrganization) and Maputo LocalBusiness (branchOf)
  const nampulaBranchSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "TaxiService"],
    "@id": "https://www.kareemtaxi.co.mz/#localbusiness-nampula",
    "name": "Kareem Taxi Nampula – Parceiro Oficial Yango",
    "parentOrganization": { "@id": "https://www.kareemtaxi.co.mz/org" },
    "branchOf": { "@id": "https://www.kareemtaxi.co.mz/business" },
    "url": "https://www.kareemtaxi.co.mz/",
    "telephone": "+258872018208",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Zia em frente da EDM, Av. do Trabalho",
      "addressLocality": "Nampula",
      "addressRegion": "Nampula",
      "postalCode": "3100",
      "addressCountry": "MZ"
    },
    "geo": { "@type": "GeoCoordinates", "latitude": -15.1136, "longitude": 39.2588 },
    "areaServed": {
      "@type": "City",
      "name": "Nampula",
      "containedIn": { "@type": "Country", "name": "Moçambique" }
    },
    "contactPoint": [
      { "@type": "ContactPoint", "telephone": "+258872018208", "contactType": "customer service", "areaServed": "MZ", "availableLanguage": ["Portuguese"] },
      { "@type": "ContactPoint", "telephone": "+258847068259", "contactType": "WhatsApp", "areaServed": "MZ", "availableLanguage": ["Portuguese"] }
    ],
    // TODO: insert live GBP URL when Nampula Google Business Profile goes live
    "sameAs": []
  }), []);

  // HARDCODED Portuguese FAQPage schema - ensures Google detects all 14 FAQs even with lazy loading
  const faqPageSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "inLanguage": "pt-MZ",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Quais são os requisitos para cadastro motorista Yango em Maputo?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Requisitos para cadastro motorista Yango em Maputo: 1) Pessoais: Idade mínima 18 anos, carta de condução válida (qualquer categoria), experiência de condução recomendada, 2) Veículo: Veículo próprio ou alugado em boas condições, 3) Documentos: Carta de condução, BI/Passaporte, livrete do veículo (todas as páginas), 4) Técnicos: Smartphone (Android 7+ ou iOS 12+) com ligação à internet. A Kareem Taxi – Parceiro Oficial Yango ajuda a verificar todos os requisitos durante o processo de cadastro motorista Yango."
        }
      },
      {
        "@type": "Question",
        "name": "Posso trabalhar com tuk-tuk na Yango em Maputo?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim, pode trabalhar com tuk-tuk na Yango em Maputo! Requisitos tuk-tuk: Licença válida para transporte de passageiros, carta de condução válida (qualquer categoria), tuk-tuk em boas condições, smartphone com internet. Melhores zonas para tuk-tuk: Maputo Central, áreas turísticas (Costa do Sol, Polana), áreas residenciais Matola. Potencial de ganhos: Até 20.000 MT/mês tempo inteiro."
        }
      },
      {
        "@type": "Question",
        "name": "Quanto posso ganhar trabalhando com Yango em Maputo?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Trabalhando com Yango em Maputo, você pode ganhar dinheiro com Yango até 25.000 MT por mês trabalhando tempo inteiro. Motoristas part-time ganham 10.000-15.000 MT mensais. A Kareem Taxi – Parceiro Oficial Yango oferece suporte para maximizar seus ganhos através de bônus semanais, horários de pico, e estratégias personalizadas."
        }
      },
      {
        "@type": "Question",
        "name": "Como funciona o pagamento motorista Yango?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sistema de pagamento Yango para motoristas: 1) Cálculo de Ganhos: Cálculo automático de tarifa por corrida, exibição transparente de comissão (~20%), rastreamento de saldo em tempo real no app Yango Pro, 2) Métodos de Pagamento: Transferências automáticas semanais para M-Pesa ou eMola, opção de saque instantâneo disponível, 3) Bônus: Bônus de desempenho semanais, incentivos de horários de pico, recompensas por indicações."
        }
      },
      {
        "@type": "Question",
        "name": "O que é o bônus semanal Yango e como posso ganhar?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Programa de bônus semanal Yango: Complete um número específico de corridas por semana (ex: 50, 80, 100+ corridas), mantenha avaliação alta (4.8+ estrelas), complete corridas durante horários de pico para bônus multiplicador. Exemplo: 50 corridas/semana = 1.000 MT bônus, 80 corridas/semana = 2.500 MT bônus, 100+ corridas/semana = 5.000 MT bônus."
        }
      },
      {
        "@type": "Question",
        "name": "Posso trabalhar como motorista Yango part-time enquanto mantenho outro emprego?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim! Muitos motoristas Yango Maputo através da Kareem Taxi – Parceiro Oficial Yango trabalham part-time. Você tem total flexibilidade para escolher quando trabalhar através do Yango Pro - fins de semana, noites ou qualquer horário conveniente. Motoristas part-time tipicamente trabalham 15-25 horas semanais e ganham 10.000-15.000 MT mensais como rendimento extra."
        }
      },
      {
        "@type": "Question",
        "name": "Qual a diferença entre Yango Táxi e Yango Delivery?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Diferenças Yango Táxi vs Yango Delivery: Yango Táxi (Carro): Transporta passageiros, requer carro 4 portas, carta de condução válida (qualquer categoria), ganhos até 25.000 MT/mês, horários de pico 7-9h & 17-20h. Yango Delivery (Mota): Entrega pacotes/comida, requer mota 125cc+, carta de condução válida (qualquer categoria), ganhos até 20.000 MT/mês, custos combustível 30-40% menores."
        }
      },
      {
        "@type": "Question",
        "name": "Como me tornar motorista Yango e começar a trabalhar em Maputo?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Passos completos para se tornar motorista Yango através da Kareem Taxi – Parceiro Oficial: 1) Verificar Requisitos: Idade 18+, carta de condução válida (qualquer categoria), veículo próprio/alugado, 2) Cadastro Rápido: Contactar Kareem Taxi +258 85 676 4504, enviar documentos via WhatsApp, cadastro aprovado em 15 minutos, 3) Baixar App: Instalar Yango Pro (Google Play/APK), 4) Ativação: Conta ativada automaticamente, formação gratuita incluída, 5) Começar a Ganhar: Aceitar primeira corrida, ganhar até 25.000 MT/mês."
        }
      },
      {
        "@type": "Question",
        "name": "Como fazer cadastro e criar conta Yango em Maputo? (Passo a passo)",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Processo completo de cadastro motorista Yango Maputo com Kareem Taxi: Passo 1 - Documentos Necessários: Carta de condução válida, BI/Passaporte, livrete do veículo (enviar TODAS as páginas). Passo 2 - Enviar Documentos: Contactar Kareem Taxi WhatsApp +258 85 676 4504, enviar fotos claras de todos os documentos. Passo 3 - Verificação Kareem Taxi: Nossa equipa verifica seus documentos e completa o cadastro com a Yango (15 minutos). Passo 4 - Baixar Yango Pro: Após aprovação, baixar Yango Pro (Google Play ou App Store). Passo 5 - Começar a Conduzir: Conta ativada, pronto para aceitar corridas!"
        }
      },
      {
        "@type": "Question",
        "name": "Como funciona o aplicativo Yango Pro para motoristas? (Guia completo)",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Aplicativo Yango Pro para motoristas - Guia completo: Funcionalidades Principais: 1) Pedidos de Corrida: Receber notificações de corrida em tempo real com localização do passageiro, destino, tarifa estimada, 2) Aceitar/Rejeitar: 15-20 segundos para aceitar corridas, ver avaliação do passageiro antes de aceitar, 3) Navegação: GPS integrado com rotas otimizadas, 4) Rastreamento de Ganhos: Painel de ganhos ao vivo, relatórios diários/semanais/mensais, 5) Sistema de Pagamento: Cálculo automático de tarifa, exibição transparente de comissão."
        }
      },
      {
        "@type": "Question",
        "name": "Como baixar e instalar Yango Pro APK em Maputo?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Guia completo para baixar e instalar Yango Pro: Método 1 - Google Play Store (Recomendado): 1) Abrir Google Play Store no smartphone, 2) Pesquisar Yango Pro ou Yango Pro Driver, 3) Clicar Instalar (gratuito), 4) Aguardar download e instalação automática, 5) Abrir app e fazer login. Método 2 - Download APK Direto: Visitar site oficial Yango (yango.com), baixar APK Yango Pro mais recente, permitir Fontes desconhecidas nas configurações, instalar APK manualmente."
        }
      },
      {
        "@type": "Question",
        "name": "A Kareem Taxi oferece formação para novos motoristas?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim! A Kareem Taxi – Parceiro Oficial Yango oferece formação completa gratuita para todos os novos motoristas Yango: 1) Domínio do App Yango Pro, 2) Estratégias de Ganhos: Melhores zonas para trabalhar em Maputo, horários de pico e padrões de demanda, como maximizar bônus semanais, 3) Sistemas de Pagamento & Recarga, 4) Atendimento ao Cliente, 5) Segurança & Conformidade. Formato: Tutoriais em vídeo online, sessões presenciais em Maputo, suporte contínuo via WhatsApp 24/7. Custo: 100% GRATUITA."
        }
      },
      {
        "@type": "Question",
        "name": "Como trabalhar como motorista Yango à noite em Maputo?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Trabalhar como motorista Yango à noite em Maputo é possível e lucrativo! Os horários noturnos (20h-6h) têm frequentemente menos concorrência e forte demanda. Dicas para condução noturna: 1) Foque em zonas como Polana, Sommerschield e áreas de entretenimento, 2) Transferências para o aeroporto são comuns de madrugada, 3) Noites de fim de semana (sexta/sábado) são mais lucrativas. Potencial de ganhos: 15-30% mais por corrida durante surtos noturnos."
        }
      },
      {
        "@type": "Question",
        "name": "O que fazer se a minha conta Yango for bloqueada?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Se a sua conta Yango for bloqueada (motorista Maputo bloqueado): Razões comuns incluem saldo negativo, documentos expirados, avaliações baixas ou violação de políticas. Como resolver: 1) Verifique o app Yango Pro para motivo específico, 2) Contacte Kareem Taxi WhatsApp +258 85 676 4504 imediatamente, 3) Forneça documentos ou explicações necessárias. Dicas de prevenção: Mantenha saldo positivo, atualize documentos antes de expirar, mantenha avaliação 4.5+ estrelas."
        }
      },
      {
        "@type": "Question",
        "name": "Como aumentar ganhos como motorista Yango em Maputo?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Como aumentar ganhos motorista Yango em Maputo: 1) Trabalhe nos horários de pico 7-9h e 17-20h dias úteis, e noites de sexta e sábado — tarifas podem ser 15-30% mais altas durante o surge, 2) Melhores zonas: Polana, Sommerschield, Baixa, Costa do Sol e o aeroporto (KMIA), 3) Mantenha avaliação 4.8+ estrelas, 4) Mantenha taxa de aceitação acima de 90% para qualificar para bónus semanais (1.000-5.000 MT extra), 5) Use o heatmap do Yango Pro para se posicionar antes dos picos, 6) Mantenha o saldo M-Pesa/eMola recarregado. A Kareem Taxi oferece formação gratuita sobre otimização de ganhos — WhatsApp +258 85 676 4504."
        }
      },
      {
        "@type": "Question",
        "name": "Como recarregar saldo Yango Pro com M-Pesa ou eMola?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Como recarregar saldo Yango Pro via M-Pesa ou eMola com a recarga automática Kareem Taxi: Envie o valor (mínimo 100 MT) via eMola para 87 932 5438 ou M-Pesa para 85 676 4504. Tem de enviar a partir do mesmo número registado na Kareem Taxi para o Yango Pro. A recarga é processada automaticamente em 1-3 minutos — sem necessidade de comprovativo. Se enviar do número errado recebe um SMS automático com instruções. Se o saldo Yango Pro ficar negativo não pode aceitar novas corridas — recarregue imediatamente."
        }
      },
      {
        "@type": "Question",
        "name": "Como trabalhar na Yango com carro alugado em Maputo?",
        "url": "https://www.kareemtaxi.co.mz/",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim, pode trabalhar na Yango com carro alugado em Maputo. Requisitos: carro de 4 portas, menos de 10 anos, em boas condições; contrato de aluguer ou autorização escrita do proprietário; livrete do veículo (todas as páginas); seguro e inspeção válidos; carta de condução válida; BI/Passaporte; idade 18+. O carro pode ser registado em seu nome mesmo que não seja proprietário, desde que o dono autorize por escrito. A Kareem Taxi regista veículos próprios e alugados em 10-15 minutos via WhatsApp +258 85 676 4504."
        }
      }
    ]
  }), []);

  return (
    <>
      <Helmet>
        <html lang="pt" />
        <title>{SEO.title}</title>
        <meta name="title" content={SEO.title} />
        
        {/* Breadcrumb Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        
        {/* Organization Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        
        {/* LocalBusiness Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>

        {/* LocalBusiness — Nampula branch */}
        <script type="application/ld+json">
          {JSON.stringify(nampulaBranchSchema)}
        </script>
        
        {/* FAQPage Structured Data - 17 hardcoded Portuguese FAQs */}
        <script type="application/ld+json">
          {JSON.stringify(faqPageSchema)}
        </script>
        
        {/* WebSite Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "https://www.kareemtaxi.co.mz/website",
            "name": "KAREEM TAXI",
            "alternateName": "Kareem Taxi",
            "url": "https://www.kareemtaxi.co.mz/",
            "inLanguage": "pt-MZ"
          })}
        </script>

        {/* WebPage Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "KAREEM TAXI - Parceiro Oficial da Yango em Maputo",
            "headline": "KAREEM TAXI - Parceiro Oficial da Yango em Maputo",
            "url": "https://www.kareemtaxi.co.mz/"
          })}
        </script>

        <meta property="og:site_name" content="KAREEM TAXI" />
        <meta name="description" content={SEO.description} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={SEO.title} />
        <meta property="og:description" content={SEO.ogDescription} />
        <meta property="og:image" content="https://www.kareemtaxi.co.mz/images/kareem-taxi-parceiro-oficial-yango-mocambique.webp" />
        <meta property="og:url" content="https://www.kareemtaxi.co.mz/" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO.title} />
        <meta name="twitter:description" content={SEO.ogDescription} />
        <meta name="twitter:image" content="https://www.kareemtaxi.co.mz/images/motorista-yango-maputo.webp" />
        
        {/* Canonical URL and hreflang alternates */}
        <link rel="canonical" href="https://www.kareemtaxi.co.mz/" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="alternate" hrefLang="pt-MZ" href="https://www.kareemtaxi.co.mz/" />
        <link rel="alternate" hrefLang="en" href="https://www.kareemtaxi.co.mz/en/" />
        <link rel="alternate" hrefLang="x-default" href="https://www.kareemtaxi.co.mz/" />
        <meta property="og:locale" content="pt_MZ" />
        
        {/* Additional Keywords Meta */}
        <meta name="keywords" content={SEO.keywords} />
      </Helmet>

      <div className="min-h-screen overflow-x-hidden max-w-[100vw]">
        <Navigation />
        <main id="main-content" role="main">
          <Hero />
          
          <Suspense fallback={null}>
            <div className="cv-auto">
              <HeroGallery />
              <About />
              <Testimonials />
              <FAQ />
              <LazyBlogSection />
              <VagasMotorista />
              <QuickLinks />
              <DriverSection />
              <div className="container mx-auto px-4 pt-8">
                <div className="max-w-3xl mx-auto bg-primary/10 border-l-4 border-primary text-primary px-4 py-3 rounded-md text-center font-semibold">
                  {t('home.nampula.badge')}
                </div>
              </div>
              <CoverageAreas />
            </div>
          </Suspense>
        </main>
        <Suspense fallback={null}>
          <Footer />
          <FloatingWhatsApp />
        </Suspense>
      </div>
    </>
  );
};

export default Index;
